import React, { useEffect, useCallback, useMemo, useState } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import { Divider, Flex, Button, Spin, Form, Space, message } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import styles from "./ExperienceBooking.module.css"
import { routes } from "router"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import GuestsSelector from "./GuestSelector"
import { DateSelector, DurationSelector } from "ui/BookingCalendar"
import { getApiErrorMessage } from "helpers/embedded"
import { getOwnSiteBookingParams } from "./helpers"
import { fetchExperienceBookingDetails, updateBookingSelections, resetBookingSelections, updateEmbeddededBooking } from "store/experiences"
import { validateWidgetDiscount } from "store/discounts"
import { oldRoutes } from "router/old-routes"
import { numberToCurrency } from "helpers/string"
import FlatRatePriceTooltip from "ui/FlatRatePriceTooltip"
import { computeDiscountAmount } from "utils/discountUtils"
import { getCardFeeFraction, getCardFixedFee, roundToCents } from "utils/bookingCalculations"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"
import { Text } from "ui/Typography"
import PromoCodeInput from "ui/PromoCodeInput"
import AddOnsSelector from "components/bookings/AddOnsSelector"
import { buildAddOnSelectionsAttributes, summarizeAddOnFees } from "components/bookings/helpers"
import { computeDepositSplit } from "utils/widgetBookingPricing"
import { normalizeTimeSlots } from "utils/experienceAvailability"
import { useDevice } from "hooks/useDevice"
import { Icon } from "ui/Icon"
import {
  enumerateDates,
  getFixedDurationOptions,
  getProgressiveAdjustment,
  isDynamicMultiDay,
  isFixedMultiDay,
  priceUnitForBooking
} from "utils/multiDayPricing"

const INFO_DATA = [
  { label: "Date", value: "Add Date" },
  { label: "Guests", value: "Add Guests" },
  { label: "Trip Length", value: "Add Trip Length" },
  { label: "Time", value: "Add Time" }
]

// ---------------------------------------------------------------------------
// Pricing helpers (pure functions, no component state) — extracted from calculateBookingTotal
// below purely to keep its cognitive complexity manageable. Each mirrors the exact sub-computation
// it replaces; nothing here changes the numbers that come out.
// ---------------------------------------------------------------------------

// Prices every guest-type rate for the whole booking via priceUnitForBooking, so a dynamic
// multi-day trip's daily rate is priced once per covered date (each with its own day/seasonal
// adjustment and progressive-taper step) while everything else is a single trip price adjusted by
// the start date. Each entry is { total, baseTotal } — the adjusted price and its unadjusted
// baseline, the latter driving the "you saved" line.
const computeAdjustedRates = (priceData, trip, dates, isPerDay) => {
  const { flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger } = priceData
  const priceUnit = (rate) => priceUnitForBooking(rate, trip, dates, isPerDay)
  return {
    flatAmount: priceUnit(flat_rate),
    pricePerPerson: priceUnit(price_per_person),
    pricePerChild: priceUnit(price_per_child),
    pricePerInfant: priceUnit(price_per_infant),
    pricePerPassenger: priceUnit(price_per_passenger)
  }
}

// Splits guestsIncluded capacity across adults -> children -> infants (in that priority order) and
// returns each group's "extra" (billed) count once the included capacity is exhausted.
const allocateIncludedCapacity = (totalCounts, guestsIncluded) => {
  let remainingCapacity = guestsIncluded
  const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)
  const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)
  const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)
  return {
    extraAdults: Math.max(0, totalCounts.adults - adultsIncluded),
    extraChildren: Math.max(0, totalCounts.children - childrenIncluded),
    extraInfants: Math.max(0, totalCounts.infants - infantsIncluded)
  }
}

const computeFlatRateBreakdown = ({ totalCounts, rates, includedGuests }) => {
  const { flatAmount, pricePerPerson, pricePerChild, pricePerInfant } = rates
  const totalGuests = totalCounts.adults + totalCounts.children + totalCounts.infants + totalCounts.passengers
  const hasGuests = totalGuests > 0 || totalCounts.passengers > 0
  if (!hasGuests) return { total: 0, baseTotal: 0, breakdown: [] }

  let total = flatAmount.total
  let baseTotal = flatAmount.baseTotal
  const breakdown = [
    {
      label: "Flat Rate",
      amount: numberToCurrency(flatAmount.total),
      rawAmount: flatAmount.total,
      pricingDetails: {
        pricing_type: "charge_flat_rate",
        included_guests: includedGuests,
        price_per_person: pricePerPerson.total,
        price_per_child: pricePerChild.total,
        price_per_infant: pricePerInfant.total
      }
    }
  ]

  const { extraAdults, extraChildren, extraInfants } = allocateIncludedCapacity(totalCounts, includedGuests)

  if (extraAdults > 0) {
    const lineTotal = extraAdults * pricePerPerson.total
    total += lineTotal
    baseTotal += extraAdults * pricePerPerson.baseTotal
    breakdown.push({ label: `Extra Adult x ${extraAdults}`, amount: numberToCurrency(lineTotal), rawAmount: lineTotal })
  }
  if (extraChildren > 0) {
    const lineTotal = extraChildren * pricePerPerson.total
    total += lineTotal
    baseTotal += extraChildren * pricePerPerson.baseTotal
    breakdown.push({ label: `Extra Child x ${extraChildren}`, amount: numberToCurrency(lineTotal), rawAmount: lineTotal })
  }
  if (extraInfants > 0) {
    const lineTotal = extraInfants * pricePerPerson.total
    total += lineTotal
    baseTotal += extraInfants * pricePerPerson.baseTotal
    breakdown.push({ label: `Extra Infant x ${extraInfants}`, amount: numberToCurrency(lineTotal), rawAmount: lineTotal })
  }
  if (totalCounts.passengers > 0) {
    const lineTotal = totalCounts.passengers * pricePerPerson.total
    total += lineTotal
    baseTotal += totalCounts.passengers * pricePerPerson.baseTotal
    breakdown.push({ label: `Passenger x ${totalCounts.passengers}`, amount: numberToCurrency(lineTotal), rawAmount: lineTotal })
  }

  return { total, baseTotal, breakdown }
}

const computeCountBreakdown = ({ totalCounts, rates }) => {
  const mapping = [
    { count: totalCounts.adults, label: "Adult", price: rates.pricePerPerson },
    { count: totalCounts.children, label: "Child", price: rates.pricePerChild },
    { count: totalCounts.infants, label: "Infant", price: rates.pricePerInfant },
    { count: totalCounts.passengers, label: "Passenger", price: rates.pricePerPassenger }
  ]
  let total = 0
  let baseTotal = 0
  const breakdown = []
  for (const { count, label, price } of mapping) {
    if (count > 0) {
      const lineTotal = count * price.total
      total += lineTotal
      baseTotal += count * price.baseTotal
      breakdown.push({ label: `${label} x ${count}`, amount: numberToCurrency(lineTotal), rawAmount: lineTotal })
    }
  }
  return { total, baseTotal, breakdown }
}

const computeGuestPricingBreakdown = ({ pricingType, totalCounts, rates, includedGuests }) => {
  if (pricingType === "charge_flat_rate") {
    return computeFlatRateBreakdown({ totalCounts, rates, includedGuests })
  }
  return computeCountBreakdown({ totalCounts, rates })
}

// A fixed-unit ($) progressive ramp is PER GUEST per ramped day (matching how the percent unit
// already scales with guest count — a solo guest and two guests must not see the same flat
// discount) — unlike a percent-unit ramp, which is scale-invariant and so stays correct when
// computeAdjustedRates embeds it in each guest type's own per-unit rate (later multiplied by
// that type's own count). A flat $ ramp can't be embedded there the same way (it would be
// multiplied AGAIN by that count, and a second time for every other guest type with a nonzero
// count too) — so it's applied here, once, off the aggregate per-day rate across all guest
// types, but pre-scaled by total guest count (see getProgressiveAdjustment), matching the
// backend (TripBooking#progressive_pricing_delta passes guest_count through to
// ProgressiveDurationPricingCalculator#delta_for_day).
const computeProgressiveFixedDeltaTotal = ({ trip, priceData, spanDates, isPerDayPricing, isDynamic, totalCounts }) => {
  if (!isPerDayPricing || !isDynamic) return 0
  if (trip?.progressive_pricing_unit !== "fixed") return 0
  const aggregatePerDayRate =
    priceData?.pricing_type === "charge_flat_rate"
      ? Number(priceData?.flat_rate) || 0
      : (totalCounts.adults || 0) * (Number(priceData?.price_per_person) || 0) +
        (totalCounts.children || 0) * (Number(priceData?.price_per_child) || 0) +
        (totalCounts.infants || 0) * (Number(priceData?.price_per_infant) || 0) +
        (totalCounts.passengers || 0) * (Number(priceData?.price_per_passenger) || 0)
  if (!aggregatePerDayRate) return 0
  const guestCount = (totalCounts.adults || 0) + (totalCounts.children || 0) + (totalCounts.infants || 0) + (totalCounts.passengers || 0)
  return spanDates.reduce((sum, _isoDate, index) => {
    const { flatDelta } = getProgressiveAdjustment(trip, index + 1, guestCount)
    if (!flatDelta) return sum
    return sum + Math.max(flatDelta, -aggregatePerDayRate)
  }, 0)
}

// Folds the progressive duration-pricing delta directly into the existing guest-type
// breakdown lines, in proportion to each line's own share of their current total —
// mirrors calculateBookingTotal.js's foldProgressiveDeltaIntoBreakdown and the backend's
// TripBooking#distribute_progressive_delta, so this preview, the Bookings-form preview,
// and the saved booking's Reservation Details all agree. Only ever called for the
// fixed/$-unit ramp — see computeProgressiveFixedDeltaTotal's own comment for why it has
// no per-guest-type basis of its own and a proportional split is the fairest available
// distribution. No separate "Progressive Duration Pricing" line.
const foldProgressiveDeltaIntoBreakdown = (breakdown, progressiveDelta) => {
  if (!progressiveDelta) return breakdown

  const baseSum = breakdown.reduce((sum, line) => sum + (line.rawAmount || 0), 0)
  if (baseSum <= 0) return breakdown

  let remainder = progressiveDelta
  const updated = breakdown.map((line) => {
    if (!line.rawAmount) return line
    const share = roundToCents(progressiveDelta * (line.rawAmount / baseSum))
    const newRaw = Math.max(0, roundToCents(line.rawAmount + share))
    remainder = roundToCents(remainder - (newRaw - line.rawAmount))
    return { ...line, rawAmount: newRaw, amount: numberToCurrency(newRaw) }
  })

  if (Math.abs(remainder) >= 0.01) {
    const largest = [...updated].sort((a, b) => (b.rawAmount || 0) - (a.rawAmount || 0))[0]
    if (largest) {
      largest.rawAmount = Math.max(0, roundToCents(largest.rawAmount + remainder))
      largest.amount = numberToCurrency(largest.rawAmount)
    }
  }

  return updated
}

const computeAddOnBreakdown = (addOnSelections, tripAddOns) => {
  let addOnsTotal = 0
  const breakdown = []
  for (const a of tripAddOns) {
    const qty = Number(addOnSelections[a.id] || 0)
    if (qty > 0) {
      const lineTotal = qty * Number(a.price ?? 0)
      addOnsTotal += lineTotal
      breakdown.push({ label: `${a.title} × ${qty}`, amount: numberToCurrency(lineTotal) })
    }
  }
  return { addOnsTotal, breakdown }
}

const computeDiscountTotal = ({ discountPromos, guestSubtotal, addOnsTotal }) => {
  const rawDiscount = (discountPromos ?? []).reduce((sum, promo) => sum + computeDiscountAmount(promo, guestSubtotal, addOnsTotal), 0)
  return Math.max(0, Math.min(rawDiscount, guestSubtotal + addOnsTotal))
}

const resolveFeeEntries = (customFees, cardFeePassthroughEnabled) => ({
  cardFeeEntry: cardFeePassthroughEnabled ? customFees.find((f) => f.title === "Card Processing Fee") : null,
  platformFeeEntry: customFees.find((f) => f.title === "Platform Fee"),
  otherFees: customFees.filter((f) => f.title !== "Card Processing Fee" && f.title !== "Platform Fee")
})

// Platform fee (commission): guest portion at the standard rate + per-add-on commission (each at
// its own commission_percentage, or the standard rate when null).
// Rounded to cents here, matching the backend's ds_commission / platform_fee_client_total
// (both decimal(10,2) columns) — the card-fee gross-up below is built from this
// already-rounded figure on the backend, not raw percentage math, so leaving this
// unrounded landed the preview a cent away from what the booking is actually charged.
const computePlatformFeeAmount = ({ platformFeeEntry, guestSubtotal, discountFactor, commissionAddOns }) => {
  if (!platformFeeEntry) return 0
  const platformRate = Number(platformFeeEntry.price) || 0
  const guestPortion = (guestSubtotal * discountFactor * platformRate) / 100
  const addOnPortion = commissionAddOns.reduce((sum, addOn) => {
    const rate = addOn.commissionPercentage != null ? addOn.commissionPercentage : platformRate
    return sum + (addOn.amount * discountFactor * rate) / 100
  }, 0)
  return roundToCents(guestPortion + addOnPortion)
}

// Other custom fees: percentage fees apply to the guest subtotal plus opted-in add-ons; fixed fees
// stay flat (charged once). Rounded to cents per fee, matching the backend's custom_fees_total
// (a decimal(10,2) column) for the same reason computePlatformFeeAmount above is.
const buildOtherFeeItems = ({ otherFees, customFeeBase }) =>
  otherFees.map((fee) => ({
    fee,
    amount: roundToCents(fee.fee_type === "percentage" ? (customFeeBase * Number(fee.price)) / 100 : Number(fee.price))
  }))

// Card processing fee applied last. Its fixed part comes from the fee entry ($0.30 on Stripe, $0.49
// for a PayPal-only guide; getCardFixedFee). It's charged only on the amount paid by card NOW: the deposit
// (depositFraction of the pre-card total) for deposit payments, or the whole pre-card total when
// paying in full. `total` at this point is the pre-card total.
// Gross-up: divide by (1 - rate) so that after Stripe deducts its fee on (depositBase + fee), the
// net equals depositBase. A plain markup would under-collect by the fee-on-fee. Rounded to cents,
// matching the backend's calculate_card_processing_fee, which ends with exactly this:
// `(fee + extra_card_transaction_fees).round(2)`.
const computeCardProcessingFee = ({ cardFeeEntry, total, depositFraction }) => {
  if (!cardFeeEntry || total <= 0) return 0
  const cardRate = Number(cardFeeEntry.price) / 100
  const depositBaseForFee = total * depositFraction
  return roundToCents((depositBaseForFee * cardRate + getCardFixedFee(cardFeeEntry)) / (1 - cardRate))
}

// Only the taxes and platform fee go in the breakdown list. The payment processing fee is left out
// entirely, whatever the fee entry's show_as_line_item says: it is a transaction fee on the amount
// being charged, so PriceBreakdown renders it below the Total, as every other screen does.
const buildFeesBreakdown = ({ hiddenFeesTotal }) =>
  hiddenFeesTotal > 0 ? [{ label: "Tax & Fees", amount: numberToCurrency(hiddenFeesTotal) }] : []

// Multi-day has no duration/time-slot step — guests can be selected as soon as date + tripLength
// are set. Single-day requires tripLength -> timeSlot before guests can be shown; the trip-length
// pill itself isn't gated on a date being picked first, since the auto-select effect that seeds
// the first duration (and its price) doesn't wait for one either.
const getSelectorVisibility = ({ isMultiDay, hasDate, hasTripLength, hasMultipleDurations, isLoading, slotsFetching, hasTimeSlot }) => {
  const showTripLength = !isMultiDay && hasMultipleDurations
  const showTime = !isMultiDay && hasDate && hasTripLength && !isLoading && !slotsFetching
  const showGuests = isMultiDay ? hasDate && hasTripLength : showTime && hasTimeSlot
  return { showTripLength, showTime, showGuests }
}

const getCollapseState = ({ bookNow, isMobile, activeTab, showGuests, detailsExpanded }) => {
  const canCollapse = bookNow && isMobile && activeTab === 1 && showGuests
  return { canCollapse, isCollapsed: canCollapse && !detailsExpanded }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PillSelector = ({ label, options, selectedValue, onSelect, error, errorMessage, activeTab, embedded }) => {
  const isDisabled = activeTab === 2

  if (isDisabled) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>{label}</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {selectedValue || "—"}
        </span>
      </Flex>
    )
  }

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>{label}</span>
        <div className={styles.pillContainer + " " + (embedded ? styles.embeddedPillContainer : "")}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.pill} ${selectedValue === option.value ? styles.selected : ""}`}
              onClick={() => onSelect(option)}
            >
              {option.value}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <Text color="var(--color-error)" style={{ fontSize: "var(--font-size-s)" }}>
          {errorMessage}
        </Text>
      )}
    </div>
  )
}

PillSelector.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })).isRequired,
  selectedValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  activeTab: PropTypes.number,
  embedded: PropTypes.bool
}

const TimeSlotSelector = ({ slots, selectedSlot, onSelect, error, activeTab, embedded }) => {
  const isDisabled = activeTab === 2

  if (isDisabled) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>Time</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {selectedSlot || "—"}
        </span>
      </Flex>
    )
  }

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>Time</span>
        {slots.length ? (
          <div className={styles.pillContainer}>
            {slots.map((slot) => (
              <button
                key={slot}
                type="button"
                className={`${styles.pill} ${selectedSlot === slot ? styles.selected : ""} ${embedded ? styles.embeddedTimePillContainer : ""}`}
                onClick={() => onSelect(slot)}
              >
                {slot}
              </button>
            ))}
          </div>
        ) : (
          <Text style={{ color: "var(--color-text-secondary-dark)" }}>No Time Slots</Text>
        )}
      </div>
      {error && (
        <Text color="var(--color-error)" style={{ fontSize: "var(--font-size-s)" }}>
          Time is required.
        </Text>
      )}
    </div>
  )
}

TimeSlotSelector.propTypes = {
  slots: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSlot: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  activeTab: PropTypes.number,
  embedded: PropTypes.bool
}

const PriceBreakdown = ({
  breakdown,
  amount,
  depositAmount,
  balanceAmount,
  selectedPayment,
  bookingDate,
  appliedPromos = [],
  baseAmount = 0,
  addOnsTotal = 0,
  savings = 0,
  t,
  cardProcessingFee = 0,
  preCardTotal = 0
}) => {
  // amount is already net of the discount (applied before tax in calculateBookingTotal); the
  // promo rows below are shown for transparency only and must NOT be subtracted again.
  const adjustedTotal = Math.max(0, amount ?? 0)
  // The Total is the booking price; the processing fee is added below it. On a deposit the fee is
  // charged on the deposit, so it hangs off that row instead — same split as the guide's screens.
  const processingFee = Math.max(0, cardProcessingFee)
  const bookingTotal = processingFee > 0 ? Math.max(0, preCardTotal) : adjustedTotal
  const isDeposit = Boolean(selectedPayment) && selectedPayment !== "full"

  const feeRows = (chargedAmount) => (
    <>
      <Flex align="center" justify="space-between">
        <Text>Payment Processing Fee</Text>
        <Text>+{numberToCurrency(processingFee)}</Text>
      </Flex>
      <Flex align="center" justify="space-between">
        <Text strong style={{ fontWeight: 600 }}>
          Total Charged to Card
        </Text>
        <Text style={{ fontWeight: 600 }}>{numberToCurrency(chargedAmount)}</Text>
      </Flex>
    </>
  )

  return (
    <>
      <Flex vertical gap={4}>
        {breakdown?.map((item) => (
          <Flex key={`${item?.label}-${item?.amount}`} align="center" justify="space-between">
            <Flex align="center" gap={4}>
              <Text>{item?.label}</Text>
              {item?.pricingDetails && <FlatRatePriceTooltip {...item.pricingDetails} />}
            </Flex>
            <Text>{item?.amount}</Text>
          </Flex>
        ))}
        {appliedPromos.map((promo) => (
          <Flex key={promo.code} align="center" justify="space-between">
            <Text type="secondary">
              {promo.name} ({promo.code})
            </Text>
            <Text className={styles.discountAmount}>-{numberToCurrency(computeDiscountAmount(promo, baseAmount, addOnsTotal))}</Text>
          </Flex>
        ))}
      </Flex>
      <Divider />
      <Flex align="center" justify="space-between">
        <Text strong style={{ fontWeight: 600 }}>
          Total
        </Text>
        <Flex align="center" gap={4}>
          {savings > 0 && <Text className={styles.originalPrice}>{numberToCurrency(bookingTotal + savings)}</Text>}
          <Text style={{ fontWeight: 600 }}>{numberToCurrency(bookingTotal)}</Text>
        </Flex>
      </Flex>
      {savings > 0 && (
        <Text className={styles.savingsNote}>{t("booking.multi_day.you_saved", { amount: numberToCurrency(savings) })}</Text>
      )}
      {processingFee > 0 && !isDeposit && feeRows(adjustedTotal)}
      {isDeposit && (
        <>
          <Flex align="center" justify="space-between">
            <Text strong style={{ fontWeight: 600 }}>
              Pay Now (Deposit)
            </Text>
            <Text style={{ fontWeight: 600 }}>{numberToCurrency(Math.max(0, depositAmount - processingFee))}</Text>
          </Flex>
          {processingFee > 0 && feeRows(depositAmount)}
          <Flex align="center" justify="space-between">
            <Text>
              {selectedPayment === "later"
                ? `Balance Due on ${moment(bookingDate).subtract(1, "day").format("MMMM D, YYYY")}`
                : `Balance Due on ${moment(bookingDate).format("MMMM D, YYYY")}`}
            </Text>
            <Text>{numberToCurrency(balanceAmount || 0)}</Text>
          </Flex>
        </>
      )}
    </>
  )
}

PriceBreakdown.propTypes = {
  breakdown: PropTypes.array,
  amount: PropTypes.number,
  depositAmount: PropTypes.number,
  balanceAmount: PropTypes.number,
  selectedPayment: PropTypes.string,
  bookingDate: PropTypes.string,
  appliedPromos: PropTypes.array,
  baseAmount: PropTypes.number,
  addOnsTotal: PropTypes.number,
  savings: PropTypes.number,
  t: PropTypes.func.isRequired,
  cardProcessingFee: PropTypes.number,
  preCardTotal: PropTypes.number
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const BookingDetailCard = ({
  bookNow,
  trip,
  bookingButtonVisible,
  activeTab,
  bookingErrors,
  setBookingErrors,
  form,
  mode,
  embedded,
  appliedPromos,
  onPromoApplied,
  onPromoRemoved,
  clientIdentifier,
  isEditingAddOns,
  setIsEditingAddOns,
  addOnsSnapshot,
  setAddOnsSnapshot
}) => {
  const [slotsFetching, setSlotsFetching] = useState(false)
  const { isMobile } = useDevice()
  // On mobile, once the user is on the booking page (activeTab === 1) the date/time/guest selectors
  // have already been chosen, so we collapse them by default to a compact summary. This keeps the
  // tall card from pushing the Contact Information form (rendered below it on mobile) out of view.
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const selectedPayment = Form.useWatch("paymentMethod", form)
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const location = useLocation()
  const bookingId = location.state?.b_uuid
  const dispatch = useDispatch()
  const [t] = useTranslation()
  const isBookNow = location?.pathname?.includes("/experience_detail") || location?.pathname?.includes("/trips")

  const {
    experienceById: { experienceDetail },
    experienceBookingData,
    experienceBookingData: { bookingSelections },
    bookingInfo
  } = useSelector((state) => state.experiences)

  const userRole = useSelector((state) => state.user.role)

  // Multi-day experiences have no duration-selection step and no per-slot time selection — the trip
  // runs across a fixed set of dates with a fixed start time. We seed tripLength from trip_prices[0]
  // (mirroring the guide-side manual flow) and go straight from date → guests.
  const durationType = experienceDetail?.duration_type
  const durationValue = experienceDetail?.duration_value
  const isMultiDay = durationType === "multi_day"
  // Fixed sells named durations (pick the duration, then one start date); dynamic sells a daily
  // rate over a start/end range the guest chooses within the experience's min/max duration.
  const isFixedDays = isFixedMultiDay(experienceDetail)
  const isDynamicDays = isDynamicMultiDay(experienceDetail)
  const minDurationDays = Number(experienceDetail?.min_duration_days) || 0
  const maxDurationDays = Number(experienceDetail?.max_duration_days) || 0
  const fixedDurationOptions = useMemo(() => getFixedDurationOptions(experienceDetail), [experienceDetail])

  // How many days the picked fixed duration covers. Pricing and the calendar both read this, so a
  // guest never sees a highlighted block that disagrees with what they're charged for. It comes
  // only from the selected booking option — falling back to the experience's duration_value would
  // paint a block on the calendar with no duration card highlighted to explain it.
  const selectedTripDays = isFixedDays ? Number(bookingSelections?.tripLength?.price?.duration_days ?? bookingSelections?.tripLength?.price?.trip_duration) || 0 : 0

  // The dates the booking actually covers — one per day for dynamic, and for fixed the selected
  // duration counted forward from the start date. Drives per-day pricing and the savings line.
  const bookedDates = useMemo(() => {
    const start = bookingSelections?.date
    if (!start || !isMultiDay) return start ? [dayjs(start).format("YYYY-MM-DD")] : []
    if (isDynamicDays) return enumerateDates(start, bookingSelections?.endDate)
    return enumerateDates(start, dayjs(start).add(Math.max(1, selectedTripDays) - 1, "day"))
  }, [bookingSelections?.date, bookingSelections?.endDate, selectedTripDays, isMultiDay, isDynamicDays])

  // Only the pricing-relevant slice of the experience, so the total memo doesn't re-run every time
  // an unrelated field on experienceDetail changes identity. duration_type/duration_mode are
  // included even though nothing here reads them directly — priceRateAcrossDates (called via
  // computeAdjustedRates -> priceUnitForBooking below) independently checks isDynamicMultiDay(trip)
  // to decide whether to apply progressive duration pricing at all, and silently answered "no" for
  // every dynamic trip when those two fields were missing here — the calendar showed the
  // progressive discount (it's built from a different, fuller trip object) but the actual total
  // never did, producing a real "Payment Information total ignores day N's discount" bug.
  const pricingTrip = useMemo(
    () => ({
      duration_type: experienceDetail?.duration_type,
      duration_mode: experienceDetail?.duration_mode,
      day_adjustments: experienceDetail?.day_adjustments,
      seasonal_adjustments: experienceDetail?.seasonal_adjustments,
      available_date_ranges: experienceDetail?.available_date_ranges,
      progressive_pricing_enabled: experienceDetail?.progressive_pricing_enabled,
      progressive_pricing_threshold: experienceDetail?.progressive_pricing_threshold,
      progressive_pricing_direction: experienceDetail?.progressive_pricing_direction,
      progressive_pricing_value: experienceDetail?.progressive_pricing_value,
      progressive_pricing_unit: experienceDetail?.progressive_pricing_unit,
      progressive_pricing_cap: experienceDetail?.progressive_pricing_cap
    }),
    [
      experienceDetail?.duration_type,
      experienceDetail?.duration_mode,
      experienceDetail?.day_adjustments,
      experienceDetail?.seasonal_adjustments,
      experienceDetail?.available_date_ranges,
      experienceDetail?.progressive_pricing_enabled,
      experienceDetail?.progressive_pricing_threshold,
      experienceDetail?.progressive_pricing_direction,
      experienceDetail?.progressive_pricing_value,
      experienceDetail?.progressive_pricing_unit,
      experienceDetail?.progressive_pricing_cap
    ]
  )
  const minGuests = experienceBookingData?.bookingData?.min_guests ?? experienceDetail?.min_guests
  const maxGuests = experienceBookingData?.bookingData?.max_guests ?? experienceDetail?.max_guests

  // The calendar's own day/seasonal + progressive price preview must read the SAME trip_price as
  // the selected duration card — passing the whole experienceDetail lets useDailyDiscountCalendar's
  // internal trip_prices lookup silently default to trip_prices[0], so switching duration cards on
  // a fixed multi-day trip updated the "From $X" summary but left every calendar day still showing
  // the first duration's rate.
  const calendarTrip = useMemo(() => {
    const base = experienceDetail || trip
    const selectedPrice = bookingSelections?.tripLength?.price
    return selectedPrice ? { ...base, trip_prices: [selectedPrice] } : base
  }, [experienceDetail, trip, bookingSelections?.tripLength?.price])

  // The card processing fee is charged on what is paid now: the whole total, or only the deposit
  // (the balance earns its own fee when it is collected).
  const depositFraction = getCardFeeFraction({
    depositOnly: !!(selectedPayment && selectedPayment !== "full"),
    depositPercent: experienceDetail?.deposit_percentage
  })

  // bookingSelections.amount is the grand total (incl the card fee). preCardTotal is the total
  // before any card fee — the base the deposit % is taken from; cardProcessingFee is the card fee
  // charged now (on the deposit, for a deposit).
  const preCardTotal = Math.max(0, bookingSelections?.preCardTotal ?? bookingSelections?.amount ?? 0)
  const cardProcessingFee = Math.max(0, bookingSelections?.cardProcessingFee || 0)
  const { depositAmount, balanceAmount } = computeDepositSplit({
    preCardTotal,
    cardProcessingFee,
    depositPercent: experienceDetail?.deposit_percentage
  })

  const calculateBookingTotal = useCallback(
    (counts, customFees = [], discountPromos = []) => {
      const priceData = bookingSelections?.tripLength?.price || {}
      const { pricing_type, included_guests, max_guests } = priceData
      const includedGuests = included_guests ?? max_guests ?? 0

      // Prices every guest-type rate for the whole booking. Dynamic multi-day is ALWAYS a per-day
      // rate (that's the whole point of "dynamic") — every covered date is priced on its own
      // day/seasonal adjustments plus its own progressive-taper step, and summed. Fixed multi-day
      // depends on how THIS duration is actually sold (its own rate_basis): "per_day" sums the
      // known day count the same way, no progressive; "total" (e.g. "py"'s $200/2-days and
      // $100/3-days, both flat packages, NOT per-day rates) prices once off the start date only,
      // same as single-day — treating every fixed duration as per-day regardless of rate_basis
      // overcharges every "total" one by summing days it was never priced to scale with. Each
      // rate's `baseTotal` is the same figure with no adjustments at all — the strikethrough the
      // guest is shown, rolled up below into "you saved".
      const isPerDayPricing = isDynamicDays || (isFixedDays && priceData?.rate_basis === "per_day")
      const rates = computeAdjustedRates(priceData, pricingTrip, bookedDates, isPerDayPricing)

      const totalCounts = counts?.main || { adults: 0, children: 0, infants: 0, passengers: 0 }

      const {
        total: guestTotal,
        baseTotal: baseGuestSubtotal,
        breakdown: guestBreakdown
      } = computeGuestPricingBreakdown({
        pricingType: pricing_type,
        totalCounts,
        rates,
        includedGuests
      })
      let total = guestTotal
      let breakdown = [...guestBreakdown]

      const fixedProgressiveTotal = computeProgressiveFixedDeltaTotal({
        trip: pricingTrip,
        priceData,
        spanDates: bookedDates,
        isPerDayPricing,
        isDynamic: isDynamicDays,
        totalCounts
      })
      if (fixedProgressiveTotal) {
        total = Math.max(0, total + fixedProgressiveTotal)
        breakdown = foldProgressiveDeltaIntoBreakdown(breakdown, fixedProgressiveTotal)
      }

      // Guest-only subtotal (used as discount base when promo does NOT apply to add-ons)
      const guestSubtotal = total

      // Add-on lines (one per selected add-on)
      const addOnSelections = bookingSelections?.addOns || {}
      const tripAddOns = trip?.add_ons || []
      const { addOnsTotal, breakdown: addOnBreakdown } = computeAddOnBreakdown(addOnSelections, tripAddOns)
      total += addOnsTotal
      breakdown.push(...addOnBreakdown)

      // Apply the discount to the pre-tax base BEFORE computing tax & fees, matching the backend.
      // Each promo's apply_to_add_ons flag decides whether add-ons are part of its discountable base.
      const discountAmount = computeDiscountTotal({ discountPromos, guestSubtotal, addOnsTotal })
      total -= discountAmount

      // Spread the discount across every taxable portion so each fee base is reduced proportionally
      // even though add-on portions can now be taxed at different rates.
      const grossBase = guestSubtotal + addOnsTotal
      const discountFactor = grossBase > 0 ? (grossBase - discountAmount) / grossBase : 1
      const netTaxableBase = grossBase - discountAmount

      // Per-add-on fee participation: the platform fee (commission_applicable/commission_percentage)
      // and the custom percentage fees (apply_custom_tax_and_fees). The card processing fee always
      // applies to every add-on (it's charged on the whole running total below).
      const { customTaxableAddOnsTotal, commissionAddOns } = summarizeAddOnFees(addOnSelections, tripAddOns)

      let cardProcessingFee = 0

      // "Card Processing Fee" is applied last on the running total. The "Platform Fee" entry is the
      // standard commission and is charged on the guest subtotal plus any commission-applicable
      // add-ons. All other custom fees include only add-ons that opted in.
      if (netTaxableBase > 0 && customFees?.length > 0) {
        // The card processing fee is only passed on to the guest when the experience's
        // platform_fee_setting opts in via card_fee_passthrough_enabled.
        const cardFeePassthroughEnabled = experienceDetail?.platform_fee_setting?.card_fee_passthrough_enabled === true
        const { cardFeeEntry, platformFeeEntry, otherFees } = resolveFeeEntries(customFees, cardFeePassthroughEnabled)

        const platformTaxAmount = computePlatformFeeAmount({ platformFeeEntry, guestSubtotal, discountFactor, commissionAddOns })
        const customFeeBase = (guestSubtotal + customTaxableAddOnsTotal) * discountFactor
        const otherFeeItems = buildOtherFeeItems({ otherFees, customFeeBase })
        const feeContributions = [{ fee: platformFeeEntry, amount: platformTaxAmount }, ...otherFeeItems]

        let hiddenFeesTotal = 0
        for (const { fee, amount } of feeContributions) {
          if (amount <= 0) continue
          total += amount
          if (fee.show_as_line_item) {
            breakdown.push({ label: fee.title, amount: numberToCurrency(amount) })
          } else {
            hiddenFeesTotal += amount
          }
        }

        cardProcessingFee = computeCardProcessingFee({ cardFeeEntry, total, depositFraction })
        total += cardProcessingFee

        breakdown.push(...buildFeesBreakdown({ hiddenFeesTotal }))
      }

      return {
        total,
        guestSubtotal,
        baseGuestSubtotal,
        addOnsTotal,
        discountAmount,
        breakdown,
        preCardTotal: total - cardProcessingFee
      }
    },
    [
      bookingSelections?.tripLength?.price,
      bookingSelections?.addOns,
      trip?.add_ons,
      experienceDetail?.platform_fee_setting?.card_fee_passthrough_enabled,
      pricingTrip,
      bookedDates,
      isDynamicDays,
      isFixedDays,
      depositFraction
    ]
  )

  useEffect(() => {
    const tripPricing = bookingSelections?.tripLength?.price
    const counts = bookingSelections?.counts
    if (tripPricing && counts) {
      const { total, guestSubtotal, baseGuestSubtotal, addOnsTotal, breakdown, preCardTotal: preCard } = calculateBookingTotal(
        counts,
        experienceDetail?.assigned_custom_fees,
        appliedPromos
      )
      dispatch(
        updateBookingSelections({
          amount: total,
          baseAmount: guestSubtotal,
          baseGuestSubtotal,
          addOnsTotal,
          breakdown,
          preCardTotal: preCard,
          cardProcessingFee: total - preCard
        })
      )
    }
  }, [
    bookingSelections?.tripLength,
    bookingSelections?.counts,
    bookingSelections?.addOns,
    appliedPromos,
    calculateBookingTotal,
    dispatch,
    experienceDetail?.assigned_custom_fees
  ])

  const handleAddOnsChange = useCallback(
    (e) => {
      dispatch(updateBookingSelections({ addOns: e?.target?.value || {} }))
    },
    [dispatch]
  )

  const [isAddOnsSaving, setIsAddOnsSaving] = useState(false)

  const handleStartEditAddOns = () => {
    setAddOnsSnapshot?.({ ...bookingSelections?.addOns })
    setIsEditingAddOns?.(true)
  }

  const handleCancelEditAddOns = () => {
    if (addOnsSnapshot) {
      dispatch(updateBookingSelections({ addOns: addOnsSnapshot }))
    }
    setAddOnsSnapshot?.(null)
    setIsEditingAddOns?.(false)
  }

  const handleSaveAddOns = async () => {
    const current = bookingSelections?.addOns || {}
    const isUnchanged = JSON.stringify(current) === JSON.stringify(addOnsSnapshot || {})
    if (isUnchanged) {
      setIsEditingAddOns?.(false)
      setAddOnsSnapshot?.(null)
      return
    }
    const bookingId = bookingInfo?.data?.uuid
    if (!bookingId) {
      setIsEditingAddOns?.(false)
      setAddOnsSnapshot?.(null)
      return
    }
    const attrs = buildAddOnSelectionsAttributes(current, bookingInfo?.data?.booking_add_ons)
    setIsAddOnsSaving(true)
    try {
      const res = await dispatch(
        updateEmbeddededBooking(bookingId, {
          ...getOwnSiteBookingParams(mode),
          booking: { booking_add_ons_attributes: attrs }
        })
      )
      if (res?.uuid) {
        setIsEditingAddOns?.(false)
        setAddOnsSnapshot?.(null)
      }
    } catch (err) {
      message.error(getApiErrorMessage(err) || t("booking.add_ons.update_failed"))
    } finally {
      setIsAddOnsSaving(false)
    }
  }

  // Builds the AddOnsSelector header action (Edit link, or Cancel/Save while editing) without a
  // nested ternary — each state is its own early return.
  const renderAddOnsHeaderExtra = () => {
    if (activeTab !== 2) return null
    if (isEditingAddOns) {
      return (
        <Space size={8}>
          <Button size="small" onClick={handleCancelEditAddOns} disabled={isAddOnsSaving}>
            {t("global.cancel")}
          </Button>
          <Button size="small" type="primary" onClick={handleSaveAddOns} loading={isAddOnsSaving}>
            {t("global.save")}
          </Button>
        </Space>
      )
    }
    return (
      <Button size="small" type="link" onClick={handleStartEditAddOns} className={styles.editLinkButton}>
        {t("global.edit")}
      </Button>
    )
  }

  // Dynamic multi-day carries a single daily-rate price, so seed it — there is no duration step.
  // Fixed multi-day opens on its shortest duration rather than on nothing, so the calendar below
  // already knows how long a block to paint and a guest who goes straight to the dates gets a
  // priced booking instead of a silent validation error.
  useEffect(() => {
    if (!isMultiDay) return
    const tripPrice = isDynamicDays ? experienceDetail?.trip_prices?.[0] : fixedDurationOptions[0]?.price
    if (!tripPrice) return
    // Only seed when nothing valid is selected yet, so a refetch of the experience can't snap a
    // guest who chose "3 Days" back to the shortest option.
    const selectedId = bookingSelections?.tripLength?.price?.id
    if (selectedId && experienceDetail?.trip_prices?.some((price) => price.id === selectedId)) return
    dispatch(updateBookingSelections({ tripLength: { key: tripPrice.id, value: "", price: tripPrice } }))
  }, [
    experienceDetail?.id,
    experienceDetail?.trip_prices,
    isMultiDay,
    isDynamicDays,
    fixedDurationOptions,
    bookingSelections?.tripLength?.price?.id,
    dispatch
  ])

  // Multi-day has no time slots, but the same endpoint carries its availability: the bookable start
  // dates and, for dynamic, how long a stay can actually run from each one. Keyed on the duration
  // rather than on a date, because the guest needs the map before picking.
  useEffect(() => {
    if (!isMultiDay || !id) return
    const tripPriceId = bookingSelections?.tripLength?.key
    if (!tripPriceId) return
    dispatch(fetchExperienceBookingDetails(id, "available_slots", tripPriceId, "", "", mode))
  }, [id, isMultiDay, bookingSelections?.tripLength?.key, dispatch, mode])

  const handleDateChange = useCallback(
    (value) => {
      const formattedDate = dayjs(value).format("YYYY-MM-DD")

      // Multi-day has no time-slot fetch keyed on duration — the trip runs across a set of dates, so
      // keep whatever duration/daily rate is already selected and move straight on to guests.
      if (isMultiDay) {
        dispatch(updateBookingSelections({ date: formattedDate, timeSlot: null }))
        if (setBookingErrors) {
          setBookingErrors((prev) => ({ ...prev, date: false, timeSlot: false, counts: false }))
        }
        return
      }

      const tripDurations = experienceBookingData?.bookingData?.trip_durations
      const currentTripLength = bookingSelections?.tripLength
      const isCurrentTripLengthValid = currentTripLength?.key != null && tripDurations?.some((duration) => duration.key === currentTripLength.key)
      // A single-day experience already has its duration pre-selected (the first option, or whatever
      // the guest since chose) well before a date is ever touched — picking a date must not wipe that
      // out. Only fall back to auto-picking here if nothing valid survived (e.g. trip_durations hadn't
      // loaded yet when the date was picked).
      const resolvedTripLength = isCurrentTripLengthValid ? currentTripLength : tripDurations?.length === 1 ? tripDurations[0] : null

      dispatch(
        updateBookingSelections({
          date: formattedDate,
          tripLength: resolvedTripLength,
          timeSlot: null
        })
      )

      if (resolvedTripLength?.key) {
        setSlotsFetching(true)
        dispatch(fetchExperienceBookingDetails(id, "available_slots", resolvedTripLength.key, formattedDate, "", mode))
      }

      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, date: false, tripLength: false, timeSlot: false, counts: false }))
      }
    },
    [
      dispatch,
      id,
      mode,
      setBookingErrors,
      experienceBookingData?.bookingData?.trip_durations,
      isMultiDay,
      bookingSelections?.tripLength
    ]
  )

  // Dynamic multi-day: the calendar hands back a { from, to } range. The start is stored as `date`
  // (every downstream consumer — slots, payload, deposit copy — already reads that) and the end in
  // `endDate`; a half-picked range clears the end so the guest can't advance on an open range.
  const handleRangeChange = useCallback(
    (range) => {
      dispatch(
        updateBookingSelections({
          date: range?.from ? dayjs(range.from).format("YYYY-MM-DD") : null,
          endDate: range?.to ? dayjs(range.to).format("YYYY-MM-DD") : null,
          timeSlot: null
        })
      )
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, date: !range?.from || !range?.to, timeSlot: false, counts: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  // Fixed multi-day: picking a duration picks that booking option's whole price record. Any start
  // date already chosen is kept — a longer duration just repaints a longer block from it.
  const handleDurationSelect = useCallback(
    (option) => {
      dispatch(updateBookingSelections({ tripLength: { key: option.price.id, value: "", price: option.price } }))
      if (setBookingErrors) setBookingErrors((prev) => ({ ...prev, tripLength: false }))
    },
    [dispatch, setBookingErrors]
  )

  const handleTripLengthChange = useCallback(
    (tripLength) => {
      const hasCounts = Object.values(bookingSelections?.counts?.main ?? {}).some((v) => v > 0)
      const minCount = experienceBookingData?.bookingData?.min_guests || 0
      const price = tripLength?.price || {}
      const fieldOrder = [
        { key: "adults", allowed: price.adults_allowed !== false },
        { key: "children", allowed: !!price.children_allowed },
        { key: "infants", allowed: !!price.infants_allowed },
        { key: "passengers", allowed: !!price.passengers_allowed }
      ]
      const firstAllowed = fieldOrder.find((f) => f.allowed)?.key || "adults"
      dispatch(
        updateBookingSelections({
          tripLength,
          timeSlot: null,
          counts: hasCounts
            ? bookingSelections?.counts
            : {
                main: {
                  adults: firstAllowed === "adults" ? minCount : 0,
                  children: firstAllowed === "children" ? minCount : 0,
                  infants: firstAllowed === "infants" ? minCount : 0,
                  passengers: firstAllowed === "passengers" ? minCount : 0
                },
                extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
              }
        })
      )

      if (tripLength?.key && bookingSelections?.date) {
        setSlotsFetching(true)
        dispatch(
          fetchExperienceBookingDetails(
            id,
            "available_slots",
            tripLength?.key,
            dayjs(bookingSelections?.date).format("YYYY-MM-DD"),
            "",
            mode
          )
        )
      }

      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, tripLength: false }))
      }
    },
    [
      dispatch,
      bookingSelections?.date,
      bookingSelections?.counts,
      mode,
      id,
      setBookingErrors,
      experienceBookingData?.bookingData?.min_guests
    ]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      dispatch(updateBookingSelections({ timeSlot: value }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, timeSlot: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      dispatch(updateBookingSelections({ counts: newCounts }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, counts: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleBookNow = useCallback(() => {
    if (!isBookNow) return

    const { date, counts, tripLength, timeSlot } = bookingSelections
    const totalGuests = ["adults", "children", "infants", "passengers"]
      .map((key) => Number(counts?.main?.[key] || 0))
      .reduce((acc, val) => acc + val, 0)
    const errors = {
      date: !date || (isDynamicDays && !bookingSelections?.endDate),
      counts: !counts?.main || totalGuests < (minGuests || 1),
      tripLength: !tripLength,
      timeSlot: isMultiDay ? false : !timeSlot
    }
    const hasErrors = Object.values(errors).some(Boolean)

    if (hasErrors) {
      setBookingErrors(errors)
      return
    }

    setBookingErrors({ date: false, counts: false, tripLength: false, timeSlot: false })

    if (mode === BOOKING_MODES.embedded || !mode) {
      navigate(routes.embeddedExperienceBooking({ uuid, id }))
    } else {
      const isClientUser = userRole === "user"
      if (isClientUser) navigate(routes.clientBookingWizardRootPath({ id }))
      else navigate({ pathname: oldRoutes.bookingWizardPath({ id }) })
    }
  }, [isBookNow, bookingSelections, minGuests, isMultiDay, isDynamicDays, setBookingErrors, mode, userRole, navigate, id, uuid])

  useEffect(() => {
    if (bookNow) {
      if (id) {
        dispatch(fetchExperienceBookingDetails(id, "trip_data", "", "", bookingId, mode))
      }
      if (isBookNow) {
        dispatch(resetBookingSelections())
      }
    }
  }, [bookNow, dispatch, id, bookingId, mode, isBookNow])

  // Mirrors the multi-day duration seed above: a single-day experience opens on its first Trip
  // Duration option (not just when there's only one) rather than waiting for the guest to pick a
  // date first, so the calendar has a real per-day rate to preview against from the start. Only
  // seeds when nothing valid is selected yet, so a refetch of trip_durations can't snap a guest
  // who already chose a different duration back to the first one.
  useEffect(() => {
    if (isMultiDay) return
    const tripDurations = experienceBookingData?.bookingData?.trip_durations
    if (!tripDurations?.length) return
    const selectedValue = bookingSelections?.tripLength?.value
    if (selectedValue && tripDurations.some((duration) => duration.value === selectedValue)) return
    handleTripLengthChange(tripDurations[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.trip_durations, isMultiDay])

  useEffect(() => {
    if (isMultiDay) return
    const timeSlots = normalizeTimeSlots(experienceBookingData?.bookingData?.data)
    if (timeSlots?.length === 1 && !bookingSelections?.timeSlot && !experienceBookingData?.loading) {
      handleTimeSlotChange(timeSlots[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.data, experienceBookingData?.loading, bookingSelections?.timeSlot, isMultiDay])

  useEffect(() => {
    if (!experienceBookingData?.loading) {
      setSlotsFetching(false)
    }
  }, [experienceBookingData?.loading])

  const tripDurations = experienceBookingData?.bookingData?.trip_durations
  const hasMultipleDurations = tripDurations?.length > 1
  // Multi-day: no trip-length pill, no time-slot step — go straight from date(s) to guests.
  // A dynamic range only counts once both ends are set; an open range has no price.
  const hasCompleteDates = isDynamicDays ? !!bookingSelections?.date && !!bookingSelections?.endDate : !!bookingSelections?.date
  const { showTripLength, showTime, showGuests } = getSelectorVisibility({
    isMultiDay,
    hasDate: hasCompleteDates,
    hasTripLength: !!bookingSelections?.tripLength,
    hasMultipleDurations,
    isLoading: !!experienceBookingData?.loading,
    slotsFetching,
    hasTimeSlot: !!bookingSelections?.timeSlot
  })
  // Fixed multi-day picks the duration first — the price, and so the guest rows, depend on it.
  const showDurationCards = isFixedDays && fixedDurationOptions.length > 1
  // Past the selection step the date/duration pickers collapse to a one-line summary.
  const isStepBehind = activeTab === 2 || activeTab === 3
  const timeSlots = normalizeTimeSlots(experienceBookingData?.bookingData?.data)

  const dateRangeValue = isDynamicDays
    ? {
        from: bookingSelections?.date ? dayjs(bookingSelections.date).toDate() : undefined,
        to: bookingSelections?.endDate ? dayjs(bookingSelections.endDate).toDate() : undefined
      }
    : undefined

  // What each guest type costs for this booking, with the pre-adjustment figure alongside so the
  // Add Guests rows can strike through the original when a discount applies.
  const guestUnitPrices = useMemo(() => {
    const price = bookingSelections?.tripLength?.price
    if (!price) return null
    const rates = {
      adults: price.price_per_person,
      children: price.price_per_child,
      infants: price.price_per_infant,
      passengers: price.price_per_passenger
    }
    // Same isPerDayPricing logic as calculateBookingTotal's priceUnit above — these two must
    // agree, or the per-guest-type rows shown here would disagree with the total they add up to.
    const isPerDayPricing = isDynamicDays || (isFixedDays && price?.rate_basis === "per_day")
    return Object.fromEntries(
      Object.entries(rates).map(([key, rate]) => [key, priceUnitForBooking(rate, pricingTrip, bookedDates, isPerDayPricing)])
    )
  }, [bookingSelections?.tripLength?.price, pricingTrip, bookedDates, isDynamicDays, isFixedDays])

  let calendarLabel = t("booking.multi_day.select_start_date")
  if (isDynamicDays) calendarLabel = t("booking.multi_day.select_start_end_date")
  else if (!isMultiDay) calendarLabel = t("booking.multi_day.choose_date")

  // What the guest would have paid with no day, seasonal or progressive pricing applied.
  const baseGuestSubtotal = bookingSelections?.baseGuestSubtotal ?? 0
  const savings = Math.max(0, baseGuestSubtotal - (bookingSelections?.baseAmount ?? 0))

  const { canCollapse, isCollapsed } = getCollapseState({ bookNow, isMobile, activeTab, showGuests, detailsExpanded })

  const infoRows = INFO_DATA.map((data, index) => (
    <Flex justify="space-between" align="center" className={`${styles.cardDetailItems} p-s rounded-s`} key={index + 1}>
      <Text>{data.label}</Text>
      <Text>{data.value}</Text>
    </Flex>
  ))

  const renderFullSelectors = () => (
    <>
      {showDurationCards && (
        <DurationSelector
          key="duration"
          options={fixedDurationOptions}
          selectedKey={bookingSelections?.tripLength?.price?.id}
          onSelect={handleDurationSelect}
          error={bookingErrors?.tripLength}
          collapsed={isStepBehind}
        />
      )}
      <DateSelector
        value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
        onChange={isDynamicDays ? handleRangeChange : handleDateChange}
        collapsed={isStepBehind}
        error={bookingErrors?.date}
        // calendarTrip prefers the freshly-fetched experienceDetail over the `trip` prop for the
        // same reason pricingTrip above does — it's the up-to-date source this component already
        // trusts for pricing-adjacent fields (day_adjustments/seasonal_adjustments included),
        // falling back to `trip` only if it hasn't loaded yet — and additionally narrows
        // trip_prices down to the currently selected duration (see calendarTrip's own comment).
        trip={calendarTrip}
        label={calendarLabel}
        isRange={isDynamicDays}
        rangeValue={dateRangeValue}
        minDays={minDurationDays}
        maxDays={maxDurationDays}
        maxSpanByDate={experienceBookingData?.bookingData?.data?.max_span_by_date}
        availableDates={experienceBookingData?.bookingData?.data?.available_dates}
        bookedDates={experienceBookingData?.bookingData?.data?.booked_dates}
        blockedReasons={experienceBookingData?.bookingData?.data?.blocked_reasons}
        spanDays={selectedTripDays}
      />
      {showTripLength && (
        <PillSelector
          key="trip-length"
          label="Trip Length"
          options={tripDurations}
          selectedValue={bookingSelections.tripLength?.value}
          onSelect={handleTripLengthChange}
          error={bookingErrors?.tripLength}
          errorMessage="Trip Length is required."
          activeTab={activeTab}
          embedded
        />
      )}
      {showTime && (
        <TimeSlotSelector
          key="time-slot"
          slots={timeSlots}
          selectedSlot={bookingSelections?.timeSlot}
          onSelect={handleTimeSlotChange}
          error={bookingErrors?.timeSlot}
          activeTab={activeTab}
          embedded
        />
      )}
      {showGuests && (
        <div key="guests" className={styles.stepFadeIn}>
          <GuestsSelector
            counts={bookingSelections?.counts}
            setCounts={handleGuestsChange}
            activeTab={activeTab}
            error={bookingErrors?.counts}
            setBookingErrors={setBookingErrors}
            maxGuests={maxGuests}
            minGuests={minGuests}
            unitPrices={guestUnitPrices}
          />
        </div>
      )}
      {showGuests && trip?.add_ons?.length > 0 && (
        <div key="add-ons" className={styles.stepFadeIn}>
          <AddOnsSelector
            addOns={trip.add_ons}
            selections={bookingSelections?.addOns || {}}
            onChange={handleAddOnsChange}
            disabled={activeTab === 2 && !isEditingAddOns}
            headerExtra={renderAddOnsHeaderExtra()}
          />
        </div>
      )}
    </>
  )

  // Pick the body without a nested ternary: info rows (preview), nothing when collapsed (the whole
  // section folds into the header), or the full selectors.
  let detailBody = renderFullSelectors()
  if (bookNow === false) {
    detailBody = infoRows
  } else if (isCollapsed) {
    detailBody = null
  }

  // Guest pricing (promo input + price breakdown), shown once guests are selected and the card
  // isn't collapsed. Kept as its own function (rather than a chained && in the JSX) to avoid
  // nesting the activeTab === 2 condition inside it.
  const renderPricingSection = () => {
    if (!bookNow || !showGuests || isCollapsed) return null
    return (
      <>
        {activeTab === 2 && (
          <PromoCodeInput
            totalAmount={bookingSelections?.baseAmount ?? 0}
            addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
            appliedPromos={appliedPromos}
            onPromoApplied={onPromoApplied}
            onPromoRemoved={onPromoRemoved}
            experienceId={id}
            clientIdentifier={clientIdentifier}
            validator={validateWidgetDiscount}
          />
        )}
        <PriceBreakdown
          breakdown={bookingSelections?.breakdown}
          amount={bookingSelections?.amount}
          depositAmount={depositAmount}
          balanceAmount={balanceAmount}
          selectedPayment={selectedPayment}
          bookingDate={bookingSelections?.date}
          appliedPromos={appliedPromos}
          baseAmount={bookingSelections?.baseAmount ?? 0}
          addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
          savings={savings}
          t={t}
          cardProcessingFee={cardProcessingFee}
          preCardTotal={preCardTotal}
        />
      </>
    )
  }

  return (
    <div className={styles.bookingDetailCardSpinner}>
      <Spin spinning={experienceBookingData?.loading}>
        <div className={`${styles.bookingCard} ${embedded ? styles.embeddedBookingCard : ""}`}>
          {!isMultiDay && !hasMultipleDurations && tripDurations?.length === 1 && <Text size="lg">{tripDurations[0]?.value}</Text>}
          <Flex gap={8} align="center" justify="space-between">
            <Flex gap={8} align="center">
              <Text fw={600} size="lg">
                {bookNow
                  ? (trip?.title ?? "")
                  : t("embedded.booking_detail_card.starting_price", { price: numberToCurrency(trip?.min_price) })}
              </Text>
              <PricingDetailsTooltip tripPrices={trip?.trip_prices} durationType={durationType} durationValue={durationValue} />
            </Flex>
            {canCollapse && (
              <Button
                type="text"
                aria-label={isCollapsed ? "Expand booking details" : "Collapse booking details"}
                className={styles.collapseToggle}
                icon={<Icon type="feather" name={isCollapsed ? "ChevronDown" : "ChevronUp"} color="currentColor" width={20} height={20} />}
                onClick={() => setDetailsExpanded((prev) => !prev)}
              />
            )}
          </Flex>
          {detailBody && (
            <Flex vertical gap={8} className={embedded ? "py-xs" : "py-xl"}>
              {detailBody}
            </Flex>
          )}
          {renderPricingSection()}
          {bookingButtonVisible && (
            <>
              <Button type="primary" disabled={!bookNow} className={styles.bookButton} onClick={handleBookNow}>
                Book Now
              </Button>
              <Flex justify="center" style={{ marginTop: "var(--spacing-xxxs)" }}>
                <Text style={{ fontSize: "var(--font-size-s)" }}>{t("webIntegration.payment_info")}</Text>
              </Flex>
            </>
          )}
        </div>
      </Spin>
    </div>
  )
}

BookingDetailCard.propTypes = {
  bookNow: PropTypes.bool,
  form: PropTypes.object,
  trip: PropTypes.shape({
    min_price: PropTypes.number,
    title: PropTypes.string,
    guide_booking_fee_percentage: PropTypes.number,
    price_per_person: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trip_prices: PropTypes.array,
    add_ons: PropTypes.array
  }),
  bookingButtonVisible: PropTypes.bool,
  activeTab: PropTypes.number,
  bookingErrors: PropTypes.object,
  setBookingErrors: PropTypes.func,
  mode: PropTypes.string,
  embedded: PropTypes.bool,
  appliedPromos: PropTypes.array,
  onPromoApplied: PropTypes.func,
  onPromoRemoved: PropTypes.func,
  clientIdentifier: PropTypes.string,
  isEditingAddOns: PropTypes.bool,
  setIsEditingAddOns: PropTypes.func,
  addOnsSnapshot: PropTypes.object,
  setAddOnsSnapshot: PropTypes.func
}

export default BookingDetailCard
